import React, { useEffect, useState } from "react";
import Header from "../Header";
import Sidebar from "../Sidebar";
import { useDispatch } from "react-redux";
import { fetchBalance, fetchSipUserData } from "../../slices/WalletSlice";
import HandleCallCenter from "./HandleCallCenter";
import { BASE_DOMAIN } from "../phone/constant";
import { SIPProvider } from "../../helper/sipProvider";
import { Outlet, useLocation } from "react-router-dom";
import SSEListener from "../../components/SSEListener";
import { CallGETAPI, DecryptToken } from "../../helper/Constants";

const normalizeSipWebSocketServer = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) {
    return "";
  }

  if (/^wss?:\/\//i.test(normalizedValue)) {
    return normalizedValue.replace(/\/+$/, "");
  }

  return `wss://${normalizedValue.replace(/^https?:\/\//i, "").replace(/\/+$/, "")}`;
};

const buildSipWebSocketServer = (domain = "", explicitWebSocketServer = "") => {
  const explicitServer = normalizeSipWebSocketServer(explicitWebSocketServer);
  if (explicitServer) {
    return explicitServer;
  }

  const normalizedDomain = String(domain || "").trim();
  if (!normalizedDomain) {
    return "";
  }

  if (/^wss?:\/\//i.test(normalizedDomain)) {
    return normalizedDomain.replace(/\/+$/, "");
  }

  const sanitizedDomain = normalizedDomain
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");

  if (/\/ws$/i.test(sanitizedDomain)) {
    return `wss://${sanitizedDomain}`;
  }

  if (/\.mctpbx\.net$/i.test(sanitizedDomain) && !/:\d+$/.test(sanitizedDomain)) {
    return `wss://${sanitizedDomain}:7443/ws`;
  }

  return `wss://${sanitizedDomain}/ws`;
};

class PrivateAreaBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error("Private area render failed:", error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="admin-page flex min-h-screen items-center justify-center px-4 text-slate-900 dark:text-white">
          <div className="admin-panel w-full max-w-lg p-6">
            <h1 className="text-xl font-semibold">Workspace could not load</h1>
            <p className="admin-muted mt-2 text-sm">
              A temporary page error occurred after login. Reload once to continue.
            </p>
            <button
              type="button"
              className="admin-primary-btn mt-5 rounded-2xl px-4 py-2 text-sm font-semibold"
              onClick={() => window.location.reload()}
            >
              Reload Workspace
            </button>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}

const Layout = () => {
  const dispatch = useDispatch();
  const [openNav, setOpenNav] = useState(false);
  const [sipProviderOptions, setSipProviderOptions] = useState(() => ({
    domain: BASE_DOMAIN || "",
    webSocketServer: window.VOIP_WS_URL || buildSipWebSocketServer(BASE_DOMAIN),
  }));
  const { pathname } = useLocation();
  const userToken = localStorage.getItem("psx_token");
  const decodedToken = DecryptToken(userToken);

  const OpenNavHandler = () => {
    setOpenNav((previousState) => !previousState);
  };

  useEffect(() => {
    const body = document.body;
    if (openNav) {
      body.classList.add("toggle-sidebar");
      return;
    }

    body.classList.remove("toggle-sidebar");
  }, [openNav]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    setOpenNav(false);
  }, [pathname]);

  useEffect(() => {
    dispatch(fetchBalance());
    dispatch(fetchSipUserData());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const syncSipProviderOptions = async () => {
      try {
        const response = await CallGETAPI("api/get-user-extension");
        if (!response.status) {
          return;
        }

        const extensionRecord = response?.data?.data || {};
        const sipDomain = extensionRecord.domain || extensionRecord.from_domain || BASE_DOMAIN || "";
        const webSocketServer =
          extensionRecord.websocket_server ||
          extensionRecord.webSocketServer ||
          window.VOIP_WS_URL ||
          buildSipWebSocketServer(sipDomain);

        if (!isMounted) {
          return;
        }

        setSipProviderOptions({
          domain: sipDomain,
          webSocketServer,
        });
      } catch (error) {
        console.error("Failed to sync SIP provider options:", error);
      }
    };

    syncSipProviderOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <PrivateAreaBoundary>
      <SIPProvider
        key={`${sipProviderOptions.domain}|${sipProviderOptions.webSocketServer}`}
        options={sipProviderOptions}
      >
        <SSEListener />
        {decodedToken?.user_id && (
          <Header openNav={openNav} navHandler={OpenNavHandler} user_id={decodedToken?.user_id} />
        )}
        <Sidebar openNav={openNav} navHandler={OpenNavHandler} />
        <HandleCallCenter />

        <div className="mt-[4rem]" />
        <div
          key={pathname}
          className="min-w-0 overflow-x-clip transition-all duration-300 lg:ml-64"
        >
          <Outlet />
        </div>
      </SIPProvider>
    </PrivateAreaBoundary>
  );
};

export default Layout;
